/**
 * Shared Kai Copilot API client for all RCM Platform services.
 */

import { getApiAuthHeaders } from '../utils/apiAuth';

const resolveApiUrl = (backendPort, frontendPort) => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_API_URL || `http://localhost:${backendPort}`;
  }

  const hostname = window.location.hostname;
  const port = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '';

  // Service frontend nginx proxies /api → backend (e.g. Claims :3153).
  if (frontendPort && String(port) === String(frontendPort)) {
    return '';
  }

  if (!isLocalhost) {
    return '';
  }

  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) return envUrl.replace(/\/api\/?$/, '');

  return `http://${hostname}:${backendPort}`;
};

export function createKaiCopilotService({ backendPort = 8154, apiBaseUrl, frontendPort } = {}) {
  const apiUrl = apiBaseUrl ?? resolveApiUrl(backendPort, frontendPort);

  // The shell hands the token over as a URL param or postMessage and it is
  // stashed in localStorage (see each service's src/index.js). Kai endpoints
  // derive the caller's user and organization from it, so requests without it
  // are rejected rather than silently scoped to someone else.
  // Read a JWT's exp without verifying it — used only to choose between two
  // tokens. The server still checks signature and expiry.
  const tokenExp = (value) => {
    try {
      const payload = String(value).split('.')[1];
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof json.exp === 'number' ? json.exp : 0;
    } catch (e) {
      return 0;
    }
  };

  const getAuthToken = () => {
    if (typeof window === 'undefined') return null;
    // Only auth_token: a bare ?token= is an email-verification / invite token
    // (see auth/EmailVerifyPage.jsx, utils/inviteRegistration.js), and treating
    // one as a bearer token would also clobber the stored session token.
    const fromUrl = new URLSearchParams(window.location.search).get('auth_token');
    const stored = localStorage.getItem('auth_token') || localStorage.getItem('token');
    // The shell refreshes the stored token via postMessage while the URL param
    // stays frozen at page load, so neither source is reliably newer — take
    // whichever expires later. Services with no bootstrap keep a stale stored
    // token forever, which is why storage alone is not enough.
    const best = tokenExp(fromUrl) > tokenExp(stored) ? fromUrl : stored || fromUrl;
    if (fromUrl && best === fromUrl) {
      try {
        localStorage.setItem('auth_token', fromUrl);
        localStorage.setItem('token', fromUrl);
      } catch (e) {
        /* storage unavailable (private mode) — use the token for this call only */
      }
    }
    return best || null;
  };

  const apiRequest = async (endpoint, options = {}) => {
    const url = `${apiUrl}/api/v1${endpoint}`;
    const token = getAuthToken();
    const defaultOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    };

    const response = await fetch(url, defaultOptions);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      if (response.status === 401) {
        throw new Error(
          errorData.detail || 'Your session has expired. Please sign in again.'
        );
      }
      if (response.status === 503 && endpoint.includes('/chat')) {
        throw new Error(
          errorData.detail ||
            'AI Copilot service is temporarily unavailable. Please try again later.'
        );
      }
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  };

  return {
    // Exposed so callers that wrap this service (e.g. the shell's routed
    // service) can hand the resolved origin to another client, instead of
    // that client re-resolving it and guessing wrong.
    apiBaseUrl: apiUrl,
    async getStatus() {
      return apiRequest('/ai-copilot/status');
    },
    async chat(message, userId = 'user', context = {}, conversationId = null) {
      return apiRequest('/ai-copilot/chat', {
        method: 'POST',
        body: JSON.stringify({
          message,
          user_id: userId,
          context,
          conversation_id: conversationId,
        }),
      });
    },
    async searchKnowledge(query, organizationId, filters = {}, k = 5) {
      const params = new URLSearchParams({ organization_id: organizationId });
      return apiRequest(`/ai-copilot/knowledge/search?${params}`, {
        method: 'POST',
        body: JSON.stringify({ query, filters, k }),
      });
    },

    async listConversations(userId, organizationId, { limit = 50, offset = 0 } = {}) {
      const params = new URLSearchParams({
        user_id: userId,
        limit: String(limit),
        offset: String(offset),
      });
      // Omit when absent rather than sending organization_id=undefined. The
      // backend derives both ids from the token and ignores these anyway; they
      // remain only for older backends that still read them.
      if (organizationId) {
        params.set('organization_id', organizationId);
      }
      return apiRequest(`/ai-copilot/conversations?${params.toString()}`);
    },

    async getConversation(conversationId, userId, organizationId) {
      const params = new URLSearchParams({
        user_id: userId,
        organization_id: organizationId,
      });
      return apiRequest(`/ai-copilot/conversations/${conversationId}?${params}`);
    },

    async updateConversation(conversationId, userId, organizationId, { title } = {}) {
      const params = new URLSearchParams({
        user_id: userId,
        organization_id: organizationId,
      });
      return apiRequest(`/ai-copilot/conversations/${conversationId}?${params}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      });
    },

    async deleteConversation(conversationId, userId, organizationId) {
      const params = new URLSearchParams({
        user_id: userId,
        organization_id: organizationId,
      });
      return apiRequest(`/ai-copilot/conversations/${conversationId}?${params}`, {
        method: 'DELETE',
      });
    },
  };
}
